import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { UserPreferences } from '@/api/entities';
import { publicOrigin } from '@/lib/publicOrigin';

/**
 * Fetches miles balance with real-time updates via Supabase Realtime.
 * Subscribes to miles_ledger changes for the given userId and refetches balance on any change.
 * Never overwrites with 0 on error - keeps last known good value to avoid glitchy UI.
 *
 * @param {string|null} userId - User ID to fetch balance for. If null, returns 0.
 * @param {object} options
 * @param {boolean} options.isOwnUser - If true, subscribes to realtime. If false (e.g. admin viewing another user), polls instead.
 * @returns {{ balance: number, isLoading: boolean, refetch: () => Promise<void> }}
 */
export function useMilesBalanceRealtime(userId, options = {}) {
  const { isOwnUser = true } = options;
  const [balance, setBalance] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimeoutRef = useRef(null);
  const lastSuccessRef = useRef(null); // Track last successful fetch to ignore stale/out-of-order responses
  const retryCountRef = useRef(0);

  const fetchBalance = useCallback(async () => {
    if (!userId) {
      setBalance(0);
      setIsLoading(false);
      lastSuccessRef.current = null;
      return;
    }
    const fetchId = Date.now();
    try {
      const { User } = await import('@/api/entities');
      const currentUser = await User.getCurrentUser();
      if (!currentUser) {
        // Don't overwrite with 0 on transient auth check - keep last known
        if (lastSuccessRef.current !== null) return;
        setBalance(0);
        return;
      }
      let b = 0;
      if (userId === currentUser.id) {
        b = await UserPreferences.getMilesBalance();
      } else if (currentUser.role === 'admin' || (currentUser.app_metadata?.roles || []).includes('admin')) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          if (lastSuccessRef.current !== null) return;
          setBalance(0);
          return;
        }
        const res = await fetch(`${publicOrigin()}/api/miles/balance/${userId}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const text = await res.text();
        let data;
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          if (res.status === 429) throw new Error('Too many requests. Please try again in a few minutes.');
          throw new Error(text || 'Failed to fetch miles balance');
        }
        if (!res.ok) {
          // API error - don't overwrite with 0, keep last known balance
          if (lastSuccessRef.current !== null) return;
          return;
        }
        b = data.balance ?? 0;
      }
      lastSuccessRef.current = fetchId;
      setBalance(typeof b === 'number' ? b : 0);
    } catch (err) {
      console.error('Failed to fetch miles balance:', err);
      // Never overwrite with 0 on error - keeps last known good value to avoid glitchy "0 miles" flash
      if (lastSuccessRef.current !== null) return;
      // First load failure: retry once after 2s (avoids showing 0 from transient network blips)
      if (retryCountRef.current < 1) {
        retryCountRef.current += 1;
        setTimeout(() => fetchBalance(), 2000);
        return;
      }
      setBalance(0);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const refetch = useCallback(() => {
    setIsLoading(true);
    return fetchBalance();
  }, [fetchBalance]);

  // Initial fetch - reset refs when userId changes so we don't show stale balance for different user
  useEffect(() => {
    if (!userId) {
      setBalance(0);
      setIsLoading(false);
      lastSuccessRef.current = null;
      retryCountRef.current = 0;
      return;
    }
    lastSuccessRef.current = null;
    retryCountRef.current = 0;
    fetchBalance();
  }, [userId, fetchBalance]);

  // Realtime subscription + polling fallback for own user; polling only for admin viewing another user
  useEffect(() => {
    if (!userId) return;

    if (isOwnUser) {
      // Supabase Realtime (instant when it works - requires migration 016: miles_ledger in supabase_realtime publication)
      const channel = supabase
        .channel(`miles_ledger_${userId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'miles_ledger',
            filter: `user_id=eq.${userId}`,
          },
          () => {
            if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = setTimeout(() => {
              fetchBalance();
            }, 200);
          }
        )
        .subscribe();

      // Polling fallback: refresh every 60s (4s was hitting rate limit; 100 req/15min shared across all API calls)
      const pollInterval = setInterval(fetchBalance, 60000);

      return () => {
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
          refreshTimeoutRef.current = null;
        }
        clearInterval(pollInterval);
        supabase.removeChannel(channel);
      };
    } else {
      // Admin viewing another user: poll every 8 seconds
      const interval = setInterval(fetchBalance, 8000);
      return () => clearInterval(interval);
    }
  }, [userId, isOwnUser, fetchBalance]);

  return { balance, isLoading, refetch };
}
